import { createSlice } from '@reduxjs/toolkit';
import {
  BASE_END_POINT_INVOICE,
  CREATE_PAYMENT_END_POINT,
  CREATE_SUBSCRIPTION_END_POINT,
  INVOICE_END_POINT,
  INVOICE_IS_PROCESSED_SUBSCRIPTION,
  INVOICE_SUBSCRIPTION_APPROVAL_URL,
  INVOICE_SUBSCRIPTION_BILL,
  IS_VALID_DRAFT,
  PLAN_TYPE,
  SUBSCRIPTION_APPROVAL_END_POINT,
  SUBSCRIPTION_PROCESS_END_POINT
} from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import { activateJobAfterPayment, clearUserJobs } from '../job/jobsSlice';
import { trackPurchase } from '../../../helpers';
import { forceRefreshPlanStatus } from '../plan/plansSlice';
import { fetchBundleHistory, fetchBundleStatus } from '../bundle/bundleSlice';

const draftInitial = {};
draftInitial[INVOICE_SUBSCRIPTION_BILL] = '';
draftInitial[INVOICE_SUBSCRIPTION_APPROVAL_URL] = '';
draftInitial[INVOICE_IS_PROCESSED_SUBSCRIPTION] = '';

const initialState = {
  current: null,
  draft: draftInitial,
  subscription: null,
  isLoading: false
};

const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    createSubscriptionBillSucceeded(state, action) {
      state.draft[INVOICE_SUBSCRIPTION_APPROVAL_URL] =
        action.payload.subscriptionApprovalUrl;
    },
    subscriptionApprovalUrlUpdated(state, action) {
      state.draft[INVOICE_SUBSCRIPTION_APPROVAL_URL] =
        action.payload.subscriptionApprovalUrl;
    },
    processSubscriptionBillSucceeded(state, action) {
      state.invoices[INVOICE_IS_PROCESSED_SUBSCRIPTION] =
        action.payload.isProcessedSubscription;
    },
    draftInvoiceUpdated(state, action) {
      state.draft = action.payload.invoicePlan;
    },
    createSubscriptionSucceeded(state, action) {
      state.isLoading = false;
      state.subscription = action.payload.subscription;

      // Extract jobId from the subscription data if available
      if (action.payload.subscription?.data?.jobId) {
        state.subscription.jobId = action.payload.subscription.data.jobId;
        console.log(
          '💼 [Invoice] Job ID stored in subscription:',
          state.subscription.jobId
        );
      } else if (action.payload.subscription?.jobId) {
        // JobId might already be at the top level
        console.log(
          '💼 [Invoice] Job ID already at top level:',
          state.subscription.jobId
        );
      } else {
        console.warn('⚠️ [Invoice] No job ID found in subscription data');
      }
    },
    createSubscriptionFailed(state) {
      state.isLoading = false;
    },
    isLoadingUpdated(state, action) {
      state.isLoading = action.payload.isLoading;
    },
    subscriptionCleared(state) {
      state.subscription = null;
      console.log('🗑️ [Invoice] Subscription cleared from state');
    }
  }
});

export const currentInvoicePlanSelector = (state) => state.invoices.current;
export const draftInvoicePlanSelector = (state) => state.invoices.draft;
export const isValidDraftInvoicePlanSelector = (state) =>
  state.invoices.draft[IS_VALID_DRAFT];
export const subscriptionBillSelector = (state) =>
  state.invoices.draft.subscriptionBill;
export const subscriptionApprovalUrlSelector = (state) =>
  state.invoices.draft.subscriptionApprovalUrl;
export const isProcessedSubscriptionSelector = (state) =>
  state.invoices.draft.isProcessedSubscription;
export const planBillSelector = (state) => state.invoices.draft.planBill;
export const subscriptionSelector = (state) => state.invoices.subscription;
export const isLoadingSelector = (state) => state.invoices.isLoading;

export const {
  createSubscriptionBillSucceeded,
  subscriptionApprovalUrlUpdated,
  processSubscriptionBillSucceeded,
  draftInvoiceUpdated,
  createSubscriptionSucceeded,
  createSubscriptionFailed,
  isLoadingUpdated,
  subscriptionCleared
} = invoicesSlice.actions;

// Thunk to create payment
export const createPayment =
  ({
    userId,
    email,
    payment_method,
    currency,
    items,
    job_id,
    metadata,
    plan_id,
    successMessage
  }) =>
  async (dispatch) => {
    console.log('🔍 [Invoice Thunk] Creating payment with jobId:', job_id);
    console.log('📦 [Invoice Thunk] Plan metadata:', metadata);
    console.log('🆔 [Invoice Thunk] Plan ID:', plan_id);
    dispatch(isLoadingUpdated({ isLoading: true }));

    try {
      const localAuth = getLocalStorageAuthentication();
      const url = `${BASE_END_POINT_INVOICE}${INVOICE_END_POINT}${CREATE_PAYMENT_END_POINT}`;

      console.log('🌐 [Invoice Thunk] Creating payment at:', url);
      console.log('📌 [Invoice Thunk] Job ID:', job_id || 'NOT PROVIDED');
      console.log(
        '👤 [Invoice Thunk] Full Name:',
        localAuth?.fullName || 'MISSING'
      );
      console.log(
        '🔑 [Invoice Thunk] JWT roles from localStorage:',
        localAuth?.roles
      );

      // Decode JWT to see what's actually in it
      if (localAuth?.token) {
        try {
          const tokenParts = localAuth.token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('🔍 [Invoice Thunk] JWT payload:', payload);
            console.log('🔍 [Invoice Thunk] JWT roles claim:', payload.roles);
          }
        } catch (e) {
          console.error('❌ [Invoice Thunk] Could not decode JWT:', e);
        }
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify({
          userId,
          email,
          full_name: localAuth?.fullName || 'User',
          payment_method,
          currency,
          items,
          job_id,
          metadata,
          plan_id
        })
      });

      console.log('📡 [Invoice Thunk] Response status:', response.status);
      console.log(
        '📡 [Invoice Thunk] Response headers:',
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        // Clone the response to read it multiple times if needed
        const responseClone = response.clone();
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorDetails = null;

        try {
          // First try to parse as JSON
          const errorData = await response.json();
          console.error('❌ [Invoice Thunk] Error response (JSON):', errorData);
          errorDetails = errorData;
          errorMessage = errorData?.message || errorData?.error || errorMessage;
        } catch (jsonError) {
          // If JSON parsing fails, try to get as text
          try {
            const errorText = await responseClone.text();
            console.error(
              '❌ [Invoice Thunk] Error response (text):',
              errorText
            );
            if (errorText) {
              errorMessage += ` - ${errorText}`;
            }
          } catch (textError) {
            console.error('❌ [Invoice Thunk] Could not parse error response');
          }
        }

        // Log full error details
        console.error('❌ [Invoice Thunk] Full error details:', {
          status: response.status,
          statusText: response.statusText,
          url: url,
          errorDetails: errorDetails,
          jwt: localAuth.token
            ? 'Token present (length: ' + localAuth.token.length + ')'
            : 'No token',
          userId: userId,
          email: email,
          fullName: localAuth?.fullName
        });

        throw new Error(errorMessage);
      }

      const data = await response.json();
      const payment = data?.data || data;

      console.log('✅ [Invoice Thunk] Payment created successfully');

      if (payment) {
        // Calculate total value from metadata
        const totalValue = items.reduce((total, item) => {
          // Each item has a price ID, we'll use a simple sum
          // You might want to calculate this from actual price data
          return total;
        }, 0);

        // Track successful payment in Google Analytics
        trackPurchase(
          payment.id || payment.paymentIntentId || 'unknown',
          totalValue || metadata?.total_price || 0,
          currency || 'USD',
          metadata?.selectedPlans || items,
          {
            job_id: job_id || 'unknown',
            payment_method: 'stripe',
            item_count: items.length,
            plan_names:
              metadata?.selectedPlans?.map((p) => p.name).join(', ') ||
              'unknown'
          }
        );

        dispatch(createSubscriptionSucceeded({ subscription: payment }));
        dispatch(
          messageUpdated({
            text:
              successMessage ||
              '✅ Payment received successfully! Activating your job post...',
            severity: Severity.SUCCESS
          })
        );

        // Clear user jobs so dashboard will refetch on next visit
        dispatch(clearUserJobs());

        // Refresh plan status to update UI with purchased profile/plan
        // This ensures the UI immediately reflects: disabled cards, applied discounts, "Your current plan" badges
        console.log('🔄 [Invoice Thunk] Refreshing plan status after payment');
        dispatch(forceRefreshPlanStatus());
        console.log(
          '🔄 [Invoice Thunk] Refreshing bundle credits after payment'
        );
        dispatch(fetchBundleStatus());
        dispatch(fetchBundleHistory());

        const selectedPlans = metadata?.selectedPlans || [];
        const hasPostJobPlan = selectedPlans.some(
          (plan) => plan?.id === PLAN_TYPE.POST_JOB
        );

        // Activate the job only for job post payments (not add-ons)
        if (job_id && hasPostJobPlan) {
          console.log(
            '💼 [Invoice Thunk] Job post payment detected, activating:',
            job_id
          );
          try {
            dispatch(activateJobAfterPayment({ jobId: job_id, userId }));
          } catch (activationError) {
            console.error(
              '❌ [Invoice Thunk] Job activation failed (but payment succeeded):',
              activationError
            );
            dispatch(
              messageUpdated({
                text: '✅ Payment successful! Please activate your job from the dashboard.',
                severity: Severity.WARNING
              })
            );
          }
        } else if (job_id) {
          console.log('ℹ️ [Invoice Thunk] Add-on payment, skipping activation');
        } else {
          console.warn(
            '⚠️ [Invoice Thunk] No job ID in payment, skipping activation'
          );
        }

        return payment;
      } else {
        throw new Error('Invalid payment data received');
      }
    } catch (error) {
      console.error('❌ [Invoice Thunk] Payment failed:', error);
      dispatch(createSubscriptionFailed());
      dispatch(
        messageUpdated({
          text: 'Payment failed, please try again',
          severity: Severity.ERROR
        })
      );
      throw error;
    }
  };

// Thunk to create subscription
export const createSubscription =
  ({ userId, email, payment_method, items }) =>
  async (dispatch) => {
    console.log('🔍 [Invoice Thunk] Creating subscription');
    dispatch(isLoadingUpdated({ isLoading: true }));

    try {
      const localAuth = getLocalStorageAuthentication();
      const url = `${BASE_END_POINT_INVOICE}${INVOICE_END_POINT}${CREATE_SUBSCRIPTION_END_POINT}`;

      console.log('🌐 [Invoice Thunk] Creating subscription at:', url);
      console.log(
        '👤 [Invoice Thunk] Full Name:',
        localAuth?.fullName || 'MISSING'
      );

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify({
          userId,
          email,
          full_name: localAuth?.fullName || 'User',
          payment_method,
          items
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const subscription = data?.data || data;

      console.log('✅ [Invoice Thunk] Subscription created successfully');

      if (subscription) {
        dispatch(createSubscriptionSucceeded({ subscription }));
        dispatch(
          messageUpdated({
            text: '✅ Subscription activated! Your payment method has been securely saved for recurring billing.',
            severity: Severity.SUCCESS
          })
        );
        // Clear user jobs so dashboard will refetch on next visit
        dispatch(clearUserJobs());
        return subscription;
      } else {
        throw new Error('Invalid subscription data received');
      }
    } catch (error) {
      console.error('❌ [Invoice Thunk] Subscription failed:', error);
      dispatch(createSubscriptionFailed());
      dispatch(
        messageUpdated({
          text: 'Subscription not created, please try again',
          severity: Severity.ERROR
        })
      );
      throw error;
    }
  };

// Thunk to create subscription bill
export const createSubscriptionBill =
  ({ subscriptionBill }) =>
  async (dispatch) => {
    console.log('🔍 [Invoice Thunk] Creating subscription bill');

    try {
      const localAuth = getLocalStorageAuthentication();
      const url = `${BASE_END_POINT_INVOICE}${INVOICE_END_POINT}${SUBSCRIPTION_APPROVAL_END_POINT}`;

      console.log('🌐 [Invoice Thunk] Creating subscription bill at:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(subscriptionBill)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const subscriptionApprovalUrl =
        data?.data?.subscriptionApprovalUrl || data?.subscriptionApprovalUrl;

      console.log('✅ [Invoice Thunk] Subscription bill created successfully');

      if (subscriptionApprovalUrl) {
        dispatch(createSubscriptionBillSucceeded({ subscriptionApprovalUrl }));
        dispatch(
          messageUpdated({
            text: 'Subscription bill created successfully',
            severity: Severity.SUCCESS
          })
        );
        return subscriptionApprovalUrl;
      } else {
        throw new Error('Invalid subscription bill data received');
      }
    } catch (error) {
      console.error('❌ [Invoice Thunk] Subscription bill failed:', error);
      dispatch(
        messageUpdated({
          text: 'Subscription bill not created, please try again',
          severity: Severity.ERROR
        })
      );
      throw error;
    }
  };

// Thunk to process subscription bill
export const processSubscriptionBill =
  ({ processSubscriptionBill }) =>
  async (dispatch) => {
    console.log('🔍 [Invoice Thunk] Processing subscription bill');

    try {
      const localAuth = getLocalStorageAuthentication();
      const url = `${BASE_END_POINT_INVOICE}${INVOICE_END_POINT}${SUBSCRIPTION_PROCESS_END_POINT}`;

      console.log('🌐 [Invoice Thunk] Processing subscription bill at:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(processSubscriptionBill)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const isProcessedSubscription =
        data?.data?.isProcessedSubscription || data?.isProcessedSubscription;

      console.log(
        '✅ [Invoice Thunk] Subscription bill processed successfully'
      );

      if (isProcessedSubscription !== undefined) {
        dispatch(processSubscriptionBillSucceeded({ isProcessedSubscription }));
        dispatch(
          messageUpdated({
            text: 'Subscription processed successfully',
            severity: Severity.SUCCESS
          })
        );
        // Clear user jobs so dashboard will refetch on next visit
        dispatch(clearUserJobs());
        return isProcessedSubscription;
      } else {
        throw new Error('Invalid subscription process data received');
      }
    } catch (error) {
      console.error('❌ [Invoice Thunk] Subscription process failed:', error);
      dispatch(
        messageUpdated({
          text: 'Subscription process failed, please try again',
          severity: Severity.ERROR
        })
      );
      throw error;
    }
  };

export default invoicesSlice.reducer;
